import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, RefreshCw, Search } from 'lucide-react';
import { alertasTexto, describeAlertaCount } from '@/features/alerts/alerts-format';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/lib/api-error';
import { cn } from '@/lib/utils';
import type { ConversacionResumen } from '@/types/conversations';
import { ConversationItems } from './ConversationItems';
import {
  BOT_FILTERS,
  CHANNEL_FILTERS,
  LEAD_FILTERS,
  ORDEN_FILTERS,
  describeRange,
  hasInboxFiltros,
  summarizeInbox,
  type BotFilter,
  type InboxFiltros,
} from './inbox-format';

/**
 * Columna izquierda del inbox: la bandeja de conversaciones (módulo 5).
 *
 * Componente de presentación: no hace peticiones. Los datos y el estado de la página los lleva
 * `InboxPage` (que es quien tiene las queries); acá solo se pintan y se avisa al padre de lo que el
 * usuario hizo (`onSelect`, `onFiltrosChange`, `onOffsetChange`).
 *
 * Los filtros van al SERVIDOR (`GET .../conversations` acepta `channel`, `estadoLead`, `botActivo`,
 * `buscar`, `orden`…): el inbox de un negocio no cabe entero en el cliente, así que la búsqueda se
 * manda con un pequeño retardo y se pagina con `limite`/`offset` (`CONVERSACIONES_POR_PAGINA`).
 */

export interface ConversationListProps {
  conversaciones: ConversacionResumen[];
  /** Total de la bandeja con los filtros puestos (lo manda el backend, no es el de la página). */
  total: number;
  offset: number;
  limite: number;
  selectedId: string | null;
  /** Ids de las conversaciones con mensajes nuevos sin ver (punto azul en la fila). */
  nuevasIds: ReadonlySet<string>;
  /** Da por vista la bandeja entera (botón del aviso de «mensajes nuevos»). */
  onMarcarVistas: () => void;
  onSelect: (conversationId: string) => void;
  filtros: InboxFiltros;
  /** Cambios parciales: el padre fusiona y vuelve a la página 0. */
  onFiltrosChange: (cambios: Partial<InboxFiltros>) => void;
  onClearFiltros: () => void;
  onOffsetChange: (offset: number) => void;
  onRefresh: () => void;
  refreshing: boolean;
  isPending: boolean;
  error: unknown;
}

/** Retardo de la búsqueda: el backend filtra por `buscar`, así que no se pide en cada tecla. */
const SEARCH_DEBOUNCE_MS = 400;

export function ConversationList({
  conversaciones,
  total,
  offset,
  limite,
  selectedId,
  nuevasIds,
  onMarcarVistas,
  onSelect,
  filtros,
  onFiltrosChange,
  onClearFiltros,
  onOffsetChange,
  onRefresh,
  refreshing,
  isPending,
  error,
}: ConversationListProps) {
  const [search, setSearch] = useState(filtros.buscar);

  // Si el filtro cambia por fuera (p. ej. "Quitar los filtros"), el input se sincroniza.
  useEffect(() => {
    setSearch(filtros.buscar);
  }, [filtros.buscar]);

  useEffect(() => {
    if (search === filtros.buscar) return undefined;

    const timer = setTimeout(() => onFiltrosChange({ buscar: search }), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, filtros.buscar, onFiltrosChange]);

  const filtering = hasInboxFiltros(filtros);

  return (
    <Card className="flex h-full flex-col">
      <CardHeader
        actions={
          <Button
            variant="ghost"
            size="icon"
            aria-label="Refrescar la bandeja"
            disabled={refreshing}
            onClick={onRefresh}
          >
            <RefreshCw size={16} className={cn(refreshing && 'animate-spin')} />
          </Button>
        }
      >
        <CardTitle>Bandeja</CardTitle>
        <CardDescription>{summarizeInbox(conversaciones, total)}</CardDescription>

        {/*
          El aviso de «mensajes nuevos» vive AQUÍ, dentro de la ventana de Conversaciones: es donde
          el asesor está cuando quiere leer quién escribió, y a diferencia de un aviso flotante no
          tapa lo que se está haciendo (los mensajes llegan todo el rato). Se apaga al abrir cada
          conversación o con el botón de al lado; las burbujas del header y del menú siguen igual.
        */}
        {nuevasIds.size > 0 ? (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-panel border border-brand-200 bg-brand-50 px-3 py-2">
            <span className="flex min-w-0 items-center gap-2 text-caption font-medium text-brand-700">
              <span aria-hidden="true" className="size-2 shrink-0 rounded-pill bg-brand-600" />
              {describeAlertaCount(nuevasIds.size)} sin ver
            </span>

            <button
              type="button"
              onClick={onMarcarVistas}
              className="shrink-0 text-micro font-semibold text-brand-700 underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
            >
              {alertasTexto.marcarVisto}
            </button>
          </div>
        ) : null}
      </CardHeader>

      <CardContent className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="space-y-3">
          <Input
            type="search"
            value={search}
            leftIcon={<Search size={16} />}
            placeholder="Buscar por teléfono o usuario…"
            aria-label="Buscar conversaciones por contacto"
            onChange={(event) => setSearch(event.target.value)}
          />

          <div className="grid grid-cols-2 gap-3">
            <Select
              aria-label="Filtrar por canal"
              value={filtros.channel}
              onChange={(event) => onFiltrosChange({ channel: event.target.value })}
            >
              {CHANNEL_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por estado del lead"
              value={filtros.estadoLead}
              onChange={(event) => onFiltrosChange({ estadoLead: event.target.value })}
            >
              {LEAD_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Select
              aria-label="Filtrar por quién contesta"
              value={filtros.bot}
              onChange={(event) => onFiltrosChange({ bot: event.target.value as BotFilter })}
            >
              {BOT_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Ordenar las conversaciones"
              value={filtros.orden}
              onChange={(event) => onFiltrosChange({ orden: event.target.value })}
            >
              {ORDEN_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>

          {filtering ? (
            <Button variant="ghost" size="sm" onClick={onClearFiltros}>
              Volver a la bandeja sin filtros
            </Button>
          ) : null}
        </div>

        {isPending ? <PageLoader label="Cargando la bandeja…" /> : null}

        {error ? <Alert>{getErrorMessage(error)}</Alert> : null}

        {!isPending && !error ? (
          <ConversationItems
            conversaciones={conversaciones}
            total={total}
            selectedId={selectedId}
            nuevasIds={nuevasIds}
            filtering={filtering}
            filtros={filtros}
            onSelect={onSelect}
            onClearFiltros={onClearFiltros}
            onFirstPage={() => onOffsetChange(0)}
          />
        ) : null}
      </CardContent>

      {total > 0 ? (
        <CardFooter className="flex items-center justify-between gap-2">
          <span className="text-caption text-ink-muted">
            {describeRange(offset, conversaciones.length, total)}
          </span>

          <span className="flex items-center gap-1">
            <Button
              variant="secondary"
              size="icon"
              aria-label="Conversaciones anteriores"
              disabled={offset === 0}
              onClick={() => onOffsetChange(Math.max(0, offset - limite))}
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              aria-label="Conversaciones siguientes"
              disabled={offset + limite >= total}
              onClick={() => onOffsetChange(offset + limite)}
            >
              <ChevronRight size={16} />
            </Button>
          </span>
        </CardFooter>
      ) : null}
    </Card>
  );
}
